import { useState } from "react";
import type { CachedContact } from "../lib/types";

export interface ContactPayload {
  contactName: string | null;
  email: string | null;
  phone: string | null;
  zip: string | null;
  marketingOptIn: boolean;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function digits(s: string): string {
  return s.replace(/\D+/g, "");
}

function formatPhone(raw: string): string {
  const d = digits(raw).slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

// Hook owns state + validation. Returns the form JSX as `fields` so parents
// can drop it into their layout without re-rendering on every keystroke
// through props. Parents read `payload` / `display` at submit time.
export function useContactFields(initial: CachedContact | null, opts: { showZip?: boolean } = {}) {
  const showZip = opts.showZip ?? true;

  const [optIn, setOptIn] = useState(initial?.marketingOptIn ?? true);
  const [name, setName] = useState(initial?.name ?? "");
  const [email, setEmail] = useState(initial?.email ?? "");
  const [phone, setPhone] = useState(initial?.phone ?? "");
  const [zip, setZip] = useState(initial?.zip ?? "");

  const phoneDigits = digits(phone);
  const emailTrim = email.trim();
  const phoneOk = phoneDigits.length === 0 || phoneDigits.length === 10;
  const emailOk = emailTrim.length === 0 || EMAIL_RE.test(emailTrim);
  const zipOk = zip.length === 0 || /^\d{5}$/.test(zip);
  const hasValidEmail = emailTrim.length > 0 && EMAIL_RE.test(emailTrim);
  const hasValidPhone = phoneDigits.length === 10;
  const contactOk = !optIn || hasValidEmail || hasValidPhone;
  const isValid = phoneOk && emailOk && zipOk && contactOk;

  const payload: ContactPayload = {
    contactName: name.trim() || null,
    email: hasValidEmail ? emailTrim.toLowerCase() : null,
    phone: hasValidPhone ? `+1${phoneDigits}` : null,
    zip: zip.trim() || null,
    marketingOptIn: optIn,
  };

  const display: CachedContact = {
    name: name.trim(),
    email: emailTrim,
    phone,
    zip: zip.trim(),
    marketingOptIn: optIn,
  };

  const fields = (
    <div className="space-y-4">
      <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-amber/30 bg-amber/5 p-4">
        <input
          type="checkbox"
          checked={optIn}
          onChange={(e) => setOptIn(e.target.checked)}
          className="mt-1 h-5 w-5 shrink-0 accent-amber"
        />
        <div className="text-sm leading-snug">
          <p className="font-semibold text-cream">Count me in for Gus' Loyalty Club.</p>
          <p className="mt-1 text-xs text-cream-dim">
            Member perks &amp; event drops. Msg &amp; data rates may apply. Reply STOP to
            unsubscribe.
          </p>
        </div>
      </label>

      <div>
        <label
          htmlFor="name"
          className="mb-1 block text-xs uppercase tracking-wider text-cream-dim"
        >
          Name
        </label>
        <input
          id="name"
          type="text"
          autoComplete="given-name"
          value={name}
          onChange={(e) => setName(e.target.value.slice(0, 80))}
          className="field"
          placeholder="First name's fine"
        />
      </div>

      {optIn && (
        <p className="text-xs text-cream-dim">Add an email or phone so we can reach the winner.</p>
      )}

      <div>
        <label
          htmlFor="email"
          className="mb-1 block text-xs uppercase tracking-wider text-cream-dim"
        >
          Email{optIn && !hasValidPhone ? " *" : ""}
        </label>
        <input
          id="email"
          type="email"
          autoComplete="email"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          value={email}
          onChange={(e) => setEmail(e.target.value.slice(0, 120))}
          className="field"
          placeholder="you@example.com"
          aria-invalid={!emailOk}
        />
        {!emailOk && <p className="mt-1 text-xs text-ember">That email doesn't look right.</p>}
      </div>

      <div>
        <label
          htmlFor="phone"
          className="mb-1 block text-xs uppercase tracking-wider text-cream-dim"
        >
          Phone{optIn && !hasValidEmail ? " *" : ""}
        </label>
        <input
          id="phone"
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          value={phone}
          onChange={(e) => setPhone(formatPhone(e.target.value))}
          className="field"
          placeholder="(555) 123-4567"
          aria-invalid={!phoneOk}
        />
        {!phoneOk && <p className="mt-1 text-xs text-ember">Enter a 10-digit US number.</p>}
      </div>

      {showZip && (
        <div>
          <label
            htmlFor="zip"
            className="mb-1 block text-xs uppercase tracking-wider text-cream-dim"
          >
            Zip
          </label>
          <input
            id="zip"
            type="text"
            inputMode="numeric"
            autoComplete="postal-code"
            maxLength={5}
            value={zip}
            onChange={(e) => setZip(digits(e.target.value).slice(0, 5))}
            className="field"
            placeholder="00000"
            aria-invalid={!zipOk}
          />
        </div>
      )}
    </div>
  );

  return { fields, payload, display, isValid };
}
